import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Group } from "@mantine/core";
import {
  PageList,
  ENUM_FORM_DISPLAY,
} from "../../components/pageList";
import ActionCellRender from "../../components/actionCellRender";
import { IconPencil, IconTrash, IconEye, IconFileAnalytics, IconBan, IconCircleCheck } from "@tabler/icons";
import { useServerApi } from "../../hooks/userServerApi";
import { useQuizBasePath } from "../../hooks/useQuizBasePath";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import QuizPageStats from "../../components/quiz/QuizPageStats";
import { quizTableColumns } from "../../components/quiz/quizTableHelpers";
import QuizStatusBadge from "../../components/quiz/QuizStatusBadge";
import { requestQuizSection } from "./quizDetail";
import { McaApprovalListBadge } from "../../components/mca/McaEntityIndicators";

const apiEntity = "quiz";

export const getQuizFormSchema = () => ({
  title: "Quiz",
  apiEntity,
  display: {
    mode: ENUM_FORM_DISPLAY.MODAL,
    size: "sm",
  },
  initialValues: { title: "" },
  layout: {
    fields: [],
  },
});

const formSchema = getQuizFormSchema();

const Quiz = () => {
  const navigate = useNavigate();
  const basePath = useQuizBasePath();
  const [api] = useServerApi();
  const tabNav = useTabNavigate();

  const listActions = useMemo(
    () => [
      {
        label: "Add New",
        onClick: () => navigate(`${basePath}/new`),
        requiresWrite: true,
      },
      {
        label: "Statistics",
        onClick: () => navigate(`${basePath}/stats`),
        variant: "default",
      },
    ],
    [basePath, navigate]
  );

  const tableSchema = useMemo(
    () => ({
      columns: [
        ...quizTableColumns().map((column) =>
          column.field === "status"
            ? {
                ...column,
                cellRender: (col, data) => (
                  <Group spacing={4} noWrap>
                    <QuizStatusBadge status={data.status} />
                    <McaApprovalListBadge entity={data} compact />
                  </Group>
                ),
              }
            : column
        ),
        {
          cellRender: ActionCellRender,
          isAction: true,
          actions: [
            { name: "view", label: "View details", icon: IconEye },
            { name: "edit", label: "Edit quiz", icon: IconPencil },
            { name: "results", label: "View results", icon: IconFileAnalytics },
          ],
          actionMenu: [
            {
              name: "disable",
              label: "Disable quiz",
              icon: IconBan,
              visible: (data) => data.status === "ACTIVE",
            },
            {
              name: "enable",
              label: "Enable quiz",
              icon: IconCircleCheck,
              visible: (data) => data.status === "CLOSED",
            },
            {
              name: "delete",
              label: "Delete quiz",
              icon: IconTrash,
              props: { color: "red" },
            },
          ],
        },
      ],
      onActionBtnClickCustom: async ({ action, data, fetchData }) => {
        switch (action) {
          case "view":
            tabNav.open({
              pathname: `${basePath}/${data._id}`,
              label: data.title ?? "Quiz",
            });
            requestQuizSection(data._id, "setting");
            break;
          case "edit":
            tabNav.open({
              pathname: `${basePath}/${data._id}/edit`,
              label: data.title ? `Edit ${data.title}` : "Edit Quiz",
            });
            break;
          case "results":
            tabNav.open({
              pathname: `${basePath}/${data._id}`,
              search: "?tab=result",
              label: data.title ?? "Quiz",
            });
            requestQuizSection(data._id, "result");
            break;
          case "disable":
            if (window.confirm(`Disable quiz "${data.title}"? Participants will no longer be able to take it.`)) {
              const res = await api.Quiz.close(data._id);
              if (res?.success) {
                toast.success("Quiz disabled");
                fetchData?.();
              } else {
                toast.error(res?.message ?? "Failed to disable quiz");
              }
            }
            break;
          case "enable":
            if (window.confirm(`Enable quiz "${data.title}"?`)) {
              const res = await api.Quiz.reactivate(data._id);
              if (res?.success) {
                toast.success("Quiz enabled");
                fetchData?.();
              } else {
                toast.error(res?.message ?? "Failed to enable quiz");
              }
            }
            break;
          case "delete":
            if (window.confirm(`Delete quiz "${data.title}"?`)) {
              await api.removeById({ apiEntity, id: data._id });
              toast.success("Deleted successfully");
              fetchData?.();
            }
            break;
          default:
            break;
        }
      },
      searchableFields: ["title", "code"],
      select: "title code status deadline mcaRequestId mcaPending",
      columnFilterLayout: "toolbar",
    }),
    [api, basePath, navigate, tabNav]
  );

  return (
    <PageList
      title="Quiz Management"
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={formSchema}
      showAddButton={false}
      customActions={listActions}
      initSort={{ by: "createdAt", order: "desc" }}
      stats={{
        component: QuizPageStats,
        props: { mb: 24 },
      }}
    />
  );
};

export default Quiz;
